import type { Ticket, TicketStatus } from '@/mocks/adminSupport';
import { statusMeta, priorityMeta } from '@/mocks/adminSupport';

interface TicketCardProps {
  ticket: Ticket;
  onClick: (ticket: Ticket) => void;
  onChangeStatus: (ticket: Ticket, status: TicketStatus) => void;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return 'Hace ' + mins + 'm';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return 'Hace ' + hrs + 'h';
  const days = Math.floor(hrs / 24);
  if (days < 7) return 'Hace ' + days + 'd';
  return new Date(dateStr).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

export default function TicketCard({ ticket, onClick, onChangeStatus }: TicketCardProps) {
  const sMeta = statusMeta[ticket.status];
  const pMeta = priorityMeta[ticket.priority];

  return (
    <button
      onClick={() => onClick(ticket)}
      className="w-full bg-background-50 rounded-2xl border border-background-200 p-4 text-left hover:border-background-300 transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Badges row */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-foreground-400 text-[10px] font-mono font-semibold">#{ticket.id}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${sMeta.bgColor} ${sMeta.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${sMeta.dot}`}></span>
              {sMeta.label}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold ${pMeta.bgColor} ${pMeta.color}`}>
              {pMeta.label}
            </span>
          </div>

          {/* Subject */}
          <h3 className="text-sm font-bold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors">
            {ticket.subject}
          </h3>

          {/* Description preview */}
          <p className="text-foreground-500 text-xs line-clamp-2 leading-relaxed">
            {ticket.description}
          </p>
        </div>

        {/* Right side meta */}
        <div className="shrink-0 text-right flex flex-col items-end gap-1.5">
          <span className="text-foreground-400 text-[10px]">{timeAgo(ticket.updatedAt)}</span>
          {ticket.replies.length > 0 ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-600 bg-sky-50 px-1.5 py-0.5 rounded-md">
              <i className="ri-chat-3-line text-[9px]"></i>
              {ticket.replies.length}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] text-foreground-300 bg-background-100 px-1.5 py-0.5 rounded-md">
              <i className="ri-chat-3-line text-[9px]"></i>
              0
            </span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-background-100">
        <span className="text-foreground-400 text-[10px] flex items-center gap-1">
          <i className="ri-user-line text-[9px]"></i>
          {ticket.requester}
        </span>
        <span className="text-foreground-400 text-[10px] flex items-center gap-1">
          <i className="ri-shield-user-line text-[9px]"></i>
          {ticket.assignedTo}
        </span>
        <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-[9px] font-semibold text-foreground-400">Ver</span>
          <i className="ri-arrow-right-line text-foreground-400 text-[10px]"></i>
        </div>
      </div>
    </button>
  );
}